import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
} from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import PaginationData from 'types/models/PaginationData';
import FirstDataConfig from 'types/models/FirstDataConfig';
import EditFDConfigContext from 'contexts/EditFDConfigContext';
import AppContext from 'contexts/AppContext';
import { getFDConfigs } from 'services/firstDataServices';
import UserRoleType from 'types/enums/UserRoleType';

export default (): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const { authData } = React.useContext(AppContext);
  const { setSelectedFDConfig } = React.useContext(EditFDConfigContext);
  const [paginationFirstDataConfig, setPaginationFirstDataConfigs] = useState<PaginationData<FirstDataConfig> | null>(
    null,
  );
  const isAdmin = authData?.user.role === UserRoleType.Admin;
  const isVenueEditor = authData?.user.role === UserRoleType.VenueEditor;

  const goToPage = (url: string): void => {
    history.push(url);
  };

  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = React.useCallback(async (page?: number) => {
    const paginationData = await getFDConfigs(page, isVenueEditor);
    if (paginationData) setPaginationFirstDataConfigs(paginationData);
  }, []);

  const renderEditButton = (firstDataConfig: FirstDataConfig): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        disabled={isVenueEditor}
        onClick={() => {
          setSelectedFDConfig(firstDataConfig);
          history.push('first-data/edit');
        }}
      >
        <Edit />
      </IconButton>
    );
  };

  const renderContent = (paginationData: FirstDataConfig[]): JSX.Element => {
    return (
      <>
        {paginationData.map((firstDataConfig: FirstDataConfig) => (
          <TableRow key={firstDataConfig.description}>
            <TableCell component="th" scope="row">
              {firstDataConfig.description}
            </TableCell>
            <TableCell className="unique-value-cell">{firstDataConfig.store}</TableCell>
            <TableCell>{firstDataConfig.timeZone}</TableCell>
            <TableCell>{firstDataConfig.currency}</TableCell>
            <TableCell>{renderEditButton(firstDataConfig)}</TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <div>
      <div className="vertical-bottom-space">
        <Button
          className="btn-50"
          variant="contained"
          color="primary"
          onClick={() => goToPage('first-data/create')}
          disabled={!isAdmin}
        >
          {t('listFDConfigs-page-create')}
        </Button>
      </div>
      <div className="vertical-bottom-space">
        <Button className="btn-50" variant="contained" color="secondary" onClick={() => history.go(-1)}>
          {t('go-back')}
        </Button>
      </div>
      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('listFDConfigs-page-description')}</TableCell>
              <TableCell>{t('listFDConfigs-page-store')}</TableCell>
              <TableCell>{t('listFDConfigs-page-timeZone')}</TableCell>
              <TableCell>{t('listFDConfigs-page-currency')}</TableCell>
              <TableCell>{t('listFDConfigs-page-edit')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationFirstDataConfig?.data && paginationFirstDataConfig?.data.length > 0 ? (
              renderContent(paginationFirstDataConfig.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('listFDConfigs-page-noConfigs')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationFirstDataConfig && (
        <TablePagination
          component="div"
          count={paginationFirstDataConfig.total}
          page={paginationFirstDataConfig.currentPage - 1}
          onPageChange={(_, page: number) => {
            loadData(page + 1);
          }}
          rowsPerPage={paginationFirstDataConfig.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </div>
  );
};
